import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function AuthPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    accountType: 'patient',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ------------------- LOGIN -------------------
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!loginData.email || !loginData.password) {
      alert('Please fill all required fields.');
      return;
    }

    try {
      const res = await fetch('https://therevulist.com/wp-json/jwt-auth/v1/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginData.email, password: loginData.password }),
      });

      const data = await res.json();
      console.log('LOGIN RESPONSE:', data);

      if (!res.ok) {
        alert(data.message || 'Login failed');
        return;
      }

      localStorage.setItem('token', data.token);
      navigate('/my-account');
    } catch (err) {
      console.error('LOGIN ERROR:', err);
      alert('Something went wrong');
    }
  };

  // ------------------- SIGNUP -------------------
  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!signupData.fullName || !signupData.email || !signupData.password) {
      alert('Please fill all required fields.');
      return;
    }
    if (signupData.password !== signupData.confirmPassword) {
      alert('Passwords do not match.');
      return;
    }
    if (signupData.password.length < 8) {
      alert('Password must be at least 8 characters.');
      return;
    }

    try {
      const res = await fetch('https://therevulist.com/wp-json/custom-api/v1/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupData),
      });

      const data = await res.json();
      console.log('REGISTER RESPONSE:', data);

      if (!res.ok) {
        alert(data.message || 'Registration failed');
        return;
      }

      alert('Success! User created.');
      navigate('/my-account');
    } catch (err) {
      console.error('SIGNUP ERROR:', err);
      alert('Something went wrong');
    }
  };

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setLoginData({ ...loginData, [e.target.name]: e.target.value });

  const handleSignupChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setSignupData({ ...signupData, [e.target.name]: e.target.value });

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-grow bg-gray-50 py-16">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Tab Switcher */}
            <div className="flex bg-gray-100 rounded-full p-1 mb-8">
              <button
                onClick={() => setActiveTab('login')}
                className={`flex-1 px-6 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'login'
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setActiveTab('signup')}
                className={`flex-1 px-6 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'signup'
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Sign Up
              </button>
            </div>

            {/* ---------------- LOGIN FORM ---------------- */}
            {activeTab === 'login' && (
              <div>
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
                  <p className="text-gray-600">Sign in to your MedReview account</p>
                </div>

                <form onSubmit={handleLoginSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <i className="ri-mail-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                      <input
                        id="login-email"
                        name="email"
                        type="email"
                        required
                        placeholder="Enter your email"
                        className="w-full pl-10 pr-4 py-3 text-base border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
                        value={loginData.email}
                        onChange={handleLoginChange}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                      <input
                        id="login-password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        placeholder="Enter your password"
                        className="w-full pl-10 pr-12 py-3 text-base border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
                        value={loginData.password}
                        onChange={handleLoginChange}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 bg-teal-600 hover:bg-teal-700 text-white shadow-sm hover:shadow-md focus:ring-teal-500 px-6 py-3 text-base"
                  >
                    Sign In
                  </button>
                </form>
              </div>
            )}

            {/* ---------------- SIGNUP FORM ---------------- */}
            {activeTab === 'signup' && (
              <div>
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Account</h1>
                  <p className="text-gray-600">Join MedReview today</p>
                </div>

                <form onSubmit={handleSignupSubmit} className="space-y-6">
                  {/* Account Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">I am a:</label>
                    <div className="grid grid-cols-2 gap-4">
                      <label className="relative flex items-center justify-center p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-teal-500 transition-colors">
                        <input
                          type="radio"
                          name="accountType"
                          value="patient"
                          checked={signupData.accountType === 'patient'}
                          onChange={handleSignupChange}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <i className="ri-user-heart-line text-3xl text-teal-600 mb-2"></i>
                          <div className="font-medium text-gray-900">Patient</div>
                        </div>
                        {signupData.accountType === 'patient' && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-teal-600 rounded-full flex items-center justify-center">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                      </label>

                      <label className="relative flex items-center justify-center p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-teal-500 transition-colors">
                        <input
                          type="radio"
                          name="accountType"
                          value="provider"
                          checked={signupData.accountType === 'provider'}
                          onChange={handleSignupChange}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <i className="ri-stethoscope-line text-3xl text-teal-600 mb-2"></i>
                          <div className="font-medium text-gray-900">Provider</div>
                        </div>
                        {signupData.accountType === 'provider' && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-teal-600 rounded-full flex items-center justify-center">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Full Name */}
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name
                    </label>
                    <div className="relative">
                      <i className="ri-user-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                      <input
                        id="fullName"
                        name="fullName"
                        type="text"
                        required
                        placeholder="Enter your full name"
                        className="w-full pl-10 pr-4 py-3 text-base border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
                        value={signupData.fullName}
                        onChange={handleSignupChange}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <i className="ri-mail-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                      <input
                        id="signup-email"
                        name="email"
                        type="email"
                        required
                        placeholder="Enter your email"
                        className="w-full pl-10 pr-4 py-3 text-base border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
                        value={signupData.email}
                        onChange={handleSignupChange}
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                      <input
                        id="signup-password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        placeholder="Create a password"
                        className="w-full pl-10 pr-12 py-3 text-base border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
                        value={signupData.password}
                        onChange={handleSignupChange}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        required
                        placeholder="Confirm your password"
                        className="w-full pl-10 pr-12 py-3 text-base border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
                        value={signupData.confirmPassword}
                        onChange={handleSignupChange}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className={showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                      </button>
                    </div>
                  </div>

                  {/* Terms */}
                  <div className="flex items-start">
                    <input
                      id="terms"
                      type="checkbox"
                      required
                      className="w-4 h-4 mt-1 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                    />
                    <label htmlFor="terms" className="ml-2 text-sm text-gray-600 cursor-pointer">
                      I agree to the{' '}
                      <a href="/terms" className="text-teal-600 hover:text-teal-700">Terms of Service</a>{' '}
                      and{' '}
                      <a href="/privacy" className="text-teal-600 hover:text-teal-700">Privacy Policy</a>
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 bg-teal-600 hover:bg-teal-700 text-white shadow-sm hover:shadow-md focus:ring-teal-500 px-6 py-3 text-base"
                  >
                    Create Account
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
